import React from 'react';
import {
  AudioFile as AudioIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  FolderSpecial as FolderSpecialIcon,
  InfoOutlined as InfoIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DAWLogo from '../../DAWLogo';
import { formatFileSize } from '../../../../utils/fileUtils';
import { ProjectSettingsMap } from '../../../../models/batchProjectUpload';
import { DetectedProject } from '../../../../utils/projectDetection';
import { useGetRenderFolderOptionsQuery } from '@/generated/graphql';
import { DAWType } from '@/types/daw';

interface ProjectCardProps {
  project: DetectedProject;
  settings: ProjectSettingsMap[string];
  isSelected: boolean;
  isExpanded: boolean;
  isUploading: boolean;
  onToggleSelect: (folderPath: string) => void;
  onToggleExpand: (folderPath: string) => void;
  onDescriptionChange: (folderPath: string, description: string) => void;
  onRendersFolderChange: (folderPath: string, folderName: string) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  settings = {},
  isSelected,
  isExpanded,
  isUploading,
  onToggleSelect,
  onToggleExpand,
  onDescriptionChange,
  onRendersFolderChange,
}) => {
  const { data: renderFolderData } = useGetRenderFolderOptionsQuery({
    fetchPolicy: 'cache-first',
  });
  const renderFolderOptions = renderFolderData?.renderFolderOptions ?? [
    'render',
    'renders',
    'bounces',
    'exports',
    'mixdown',
    'mixdowns',
  ];

  const _toggleSelectAndExpand = (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation();
    onToggleSelect(project.folderPath);
  };

  React.useEffect(() => {
    // If the project is de-selected, collapse it
    if (!isSelected && isExpanded) {
      onToggleExpand(project.folderPath);
    }

    // If the project is selected, expand it
    if (isSelected && !isExpanded) {
      onToggleExpand(project.folderPath);
    }
  }, [isSelected]);

  return (
    <Paper
      id={`project-card-${project.folderPath}`}
      elevation={isSelected ? 4 : 1}
      sx={{
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: (theme) => {
          if (!isSelected) return theme.palette.divider;
          if (settings?.isDuplicate) return theme.palette.error.main;
          return theme.palette.primary.main;
        },
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          elevation: 3,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        p={1}
        sx={{
          cursor: isUploading ? 'not-allowed' : 'pointer',
          opacity: isUploading ? 0.6 : 1,
        }}
        onClick={isUploading ? undefined : _toggleSelectAndExpand}
      >
        <Checkbox
          checked={isSelected}
          onChange={() => _toggleSelectAndExpand()}
          disabled={isUploading}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{ mr: 1 }}
          icon={<UncheckedIcon />}
          checkedIcon={<CheckCircleIcon color="primary" />}
        />

        <FolderIcon sx={{ color: 'primary.main', mr: 1, fontSize: 40 }} />

        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={0.25}>
            <Typography variant="subtitle1" fontWeight="600" fontSize="0.95rem">
              {project.projectName}
            </Typography>
            {project.dawType && (
              <DAWLogo dawType={project.dawType} variant="chip" size="medium" />
            )}
            {settings?.isDuplicate && (
              <Chip
                size="small"
                icon={<WarningIcon />}
                label="Duplicate"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {project.dawType !== DAWType.STEMS && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <AudioIcon sx={{ fontSize: '0.75rem' }} color="action" />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.75rem"
                >
                  {project.dawFiles.length} DAW files
                </Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              fontSize="0.75rem"
            >
              {project.fileCount} total files
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontSize="0.75rem"
            >
              {formatFileSize(project.totalSize)}
            </Typography>
            {project.dawType !== DAWType.STEMS && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize="0.7rem"
                sx={{ ml: 'auto' }}
              >
                /{project.folderPath}
              </Typography>
            )}
          </Box>

          {settings?.isDuplicate && (
            <Typography
              variant="caption"
              color="warning.main"
              display="block"
              mt={0.25}
              fontSize="0.7rem"
            >
              ⚠️ A project with name &quot;{settings.projectName}&quot; already
              exists
            </Typography>
          )}
        </Box>

        {isSelected && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(project.folderPath);
            }}
            disabled={isUploading}
            sx={{
              ml: 0.5,
              p: 0.5,
              backgroundColor: 'primary.100',
              '&:hover': { backgroundColor: 'primary.200' },
            }}
          >
            {isExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      {isSelected && !settings?.isDuplicate && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box px={1.5} pb={1}>
            <Divider sx={{ mb: 1 }} />

            <Box display="flex" flexDirection="column" gap={1}>
              {project.dawType !== DAWType.STEMS && (
                <Box display="flex" alignItems="flex-end" gap={1.5}>
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <FolderSpecialIcon
                        sx={{
                          fontSize: '0.9rem',
                          color: project.rendersFolder
                            ? 'success.main'
                            : 'primary.main',
                        }}
                      />
                      <Typography variant="caption" fontWeight={600}>
                        Renders folder
                      </Typography>
                      <Tooltip
                        title="Studio Vault uses this folder to find your latest exported audio for the waveform preview shown in the project list."
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: '0.75rem',
                            color: 'info.main',
                            cursor: 'help',
                            '&:hover': { color: 'text.secondary' },
                          }}
                        />
                      </Tooltip>
                    </Box>
                    {project.rendersFolder ? (
                      <Chip
                        size="small"
                        label={`${project.rendersFolder}/`}
                        color="success"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          alignSelf: 'flex-start',
                        }}
                      />
                    ) : (
                      <Select
                        size="small"
                        value={settings?.rendersFolderName ?? 'renders'}
                        disabled={isUploading}
                        onChange={(e) =>
                          onRendersFolderChange(
                            project.folderPath,
                            e.target.value
                          )
                        }
                        sx={{ fontSize: '0.78rem', minWidth: 110, height: 28 }}
                      >
                        {renderFolderOptions.map((name) => (
                          <MenuItem
                            key={name}
                            value={name}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            {name}/
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                size="small"
                label="Description"
                multiline
                rows={2}
                value={settings?.description || ''}
                onChange={(e) =>
                  onDescriptionChange(project.folderPath, e.target.value)
                }
                disabled={isUploading}
                placeholder="Add a description..."
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Box>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

export default ProjectCard;
